import React, { Component, ErrorInfo, ReactNode } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Paper, 
  Alert, 
  AlertTitle,
  Link
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error | null;
  errorInfo?: ErrorInfo | null;
  isApiConnectionError: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { 
      hasError: false,
      error: null,
      errorInfo: null,
      isApiConnectionError: false
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Check if this is a network connection error to the API
    const isApiConnectionError = error.message.includes('ECONNREFUSED') || 
                               error.message.includes('Failed to fetch') ||
                               error.message.includes('Network Error') ||
                               error.message.includes('Backend connection failed');
    
    return { 
      hasError: true, 
      error,
      isApiConnectionError
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log the error to console
    console.error('Error caught by ErrorBoundary:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo,
      isApiConnectionError: error.message.includes('ECONNREFUSED') || 
                          error.message.includes('Failed to fetch') ||
                          error.message.includes('Network Error') ||
                          error.message.includes('Backend connection failed')
    });
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null, errorInfo: null, isApiConnectionError: false });
    window.location.reload();
  }

  render(): ReactNode {
    if (this.state.hasError) {
      // If API connection error, show specialized message
      if (this.state.isApiConnectionError) {
        return (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100vh', 
            p: 3 
          }}>
            <Paper elevation={3} sx={{ 
              p: 4, 
              maxWidth: 600, 
              width: '100%',
              borderRadius: 2,
              border: '1px solid #f44336',
              mb: 4
            }}>
              <Alert severity="error" sx={{ mb: 3 }}>
                <AlertTitle>API Connection Error</AlertTitle>
                The application couldn't connect to the backend API server.
              </Alert>
              
              <Typography variant="h5" gutterBottom color="error">
                Backend Server Not Running
              </Typography>
              
              <Typography variant="body1" paragraph>
                This error usually occurs when the backend server isn't running or isn't accessible. 
                Please make sure the backend server is running on <strong>localhost:5000</strong>.
              </Typography>
              
              <Box sx={{ mt: 2, mb: 3, bgcolor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                <Typography variant="subtitle2" fontFamily="monospace">
                  1. Check if the backend server is running<br/>
                  2. Make sure it's listening on port 5000<br/>
                  3. Verify there are no firewall issues blocking the connection
                </Typography>
              </Box>
              
              <Typography variant="body2" paragraph>
                You can start the backend server by running the <strong>start-backend.ps1</strong> script 
                in the EQ/backend directory, or use the <strong>start-dev-environment.ps1</strong> script 
                to start both frontend and backend.
              </Typography>
              
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={this.handleRetry}
                  startIcon={<RefreshIcon />}
                >
                  Retry Connection
                </Button>
              </Box>
            </Paper>
          </Box>
        );
      }
      
      // For other errors, show generic error UI or custom fallback
      if (this.props.fallback) {
        return this.props.fallback;
      }
      
      return (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh', 
          p: 3 
        }}>
          <Paper elevation={3} sx={{ 
            p: 4, 
            maxWidth: 600, 
            width: '100%',
            borderRadius: 2
          }}>
            <Typography variant="h5" gutterBottom color="error">
              Something went wrong
            </Typography>
            
            <Typography variant="body1" paragraph>
              An error occurred in the application. Please try refreshing the page.
            </Typography>
            
            <Button
              variant="contained"
              color="primary"
              onClick={this.handleRetry}
              startIcon={<RefreshIcon />}
            >
              Refresh Page
            </Button>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary; 